import { useEffect, useState } from "react";
import { Box, Button, IconButton, MenuItem, Select, Tooltip, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { EvidenceOutline, StorefrontOption } from "../../types/evidence";
import { textLinkSx } from "../common/textLinkStyles";
import { storefrontSetupHref } from "../../utils/storefrontLinks";

const REMOVED = "Removed storefront";
const UNRESOLVED_NOTE = "None matched this order. The document prints without a logo, policies or checkout page.";
const EDIT_SETTINGS = "Edit storefront settings";
const NO_STOREFRONTS ="No storefronts set up yet.";
// Said beside the control like the customer contact's failed save: a toast is gone in four seconds.
const PICK_SAVE_FAILED = "Not saved — this is the storefront the server holds. Pick it again.";

// The storefront the document prints, picked here when the order named none it could match.
export default function CaseStorefrontRow({ storefront, unresolved, saved, disabled, settingsLink, onSave }: {
  storefront: EvidenceOutline["storefront"];
  /** The run raised `storefront_unresolved`; a snapshot without a storefront id is not reason enough. */
  unresolved: boolean;
  /** The agent's pick as the draft holds it; fresher than the outline's copy right after a save. */
  saved: number | null;
  disabled: boolean;
  /** The user can open the company page the storefront settings live on. */
  settingsLink: boolean;
  /** Resolves false when the server did not take the value. */
  onSave: (storefrontId: number | null) => Promise<boolean>;
}) {
  const { printedId, printedName, companyId, options } = storefront;
  const [chosen, setChosen] = useState<number | null>(saved);
  const [editing, setEditing] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => { setChosen(saved); }, [saved]);

  const shownId = chosen ?? printedId;
  // The domain tells apart two storefronts an operator gave the same name.
  const labelOf = (option: StorefrontOption) => {
    const shared = options.some((other) => other !== option && other.name === option.name);
    return option.domain && (shared || option.domain !== option.name) ? `${option.name} (${option.domain})` : option.name;
  };
  const nameOf = (id: number) => {
    const option = options.find((candidate) => candidate.storefrontId === id);
    return option ? labelOf(option) : REMOVED;
  };
  const listed = (id: number | null) => id !== null && options.some((option) => option.storefrontId === id);

  const choose = async (value: number | "") => {
    const next = value === "" ? null : value;
    setEditing(false);
    setFailed(false);
    setChosen(next);
    if (await onSave(next)) return;
    setChosen(saved);
    setFailed(true);
  };

  // Nothing to show but the ask: no storefront matched and the agent has not picked one.
  const asking = unresolved && chosen === null;
  const empty = options.length === 0;
  const shownName = shownId !== null ? nameOf(shownId) : printedName ?? REMOVED;
  // A pick whose storefront was deleted since stays selectable, so the select holds its value and None can clear it.
  const removedPick = chosen !== null && !listed(chosen);
  const value: number | "" = removedPick || listed(shownId) ? shownId ?? "" : "";

  const select = (
    <Select<number | "">
      size="small"
      fullWidth
      displayEmpty
      value={value}
      defaultOpen={editing}
      onClose={() => setEditing(false)}
      onChange={(event) => { void choose(event.target.value); }}
      disabled={disabled || empty}
      inputProps={{ "aria-label": "Storefront" }}
      renderValue={(selected) => (selected === ""
        ? <Typography component="span" variant="body2" color="text.secondary">Pick a storefront</Typography>
        : nameOf(selected))}
      sx={asking ? { "& .MuiOutlinedInput-notchedOutline": { borderColor: "warning.main" } } : undefined}
    >
      {chosen !== null && <MenuItem value="">None</MenuItem>}
      {removedPick && <MenuItem value={chosen} disabled>{REMOVED}</MenuItem>}
      {options.map((option) => (
        <MenuItem key={option.storefrontId} value={option.storefrontId}>{labelOf(option)}</MenuItem>
      ))}
    </Select>
  );

  return (
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      {asking || editing ? select : (
        // The link follows the name like the copy icons on the rows above; Change sits on the first line.
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Typography component="div" sx={{ fontSize: 13, lineHeight: "20px", fontWeight: 600, color: "#1d2939", wordBreak: "break-word", minWidth: 0 }}>
            {shownName}
            {settingsLink && companyId !== null && (
              // Glued to the last word with a no-break space, so the icon never wraps onto a line of its own.
              <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                {"\u00a0"}
                <Tooltip title={EDIT_SETTINGS}>
                  <IconButton
                    size="small"
                    href={storefrontSetupHref({ companyId, storefrontId: listed(shownId) ? shownId : null })}
                    target="_blank"
                    rel="noopener"
                    aria-label={EDIT_SETTINGS}
                    sx={{ p: 0.25, verticalAlign: "text-bottom" }}
                  >
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Typography>
          <Button size="small" variant="text" sx={{ ...textLinkSx, lineHeight: "20px", flexShrink: 0 }} disabled={disabled} onClick={() => setEditing(true)}>Change</Button>
        </Box>
      )}
      {asking && (
        <Typography variant="caption" color="warning.dark" sx={{ display: "block", mt: 0.5 }}>
          {empty ? NO_STOREFRONTS : UNRESOLVED_NOTE}
        </Typography>
      )}
      {failed && <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>{PICK_SAVE_FAILED}</Typography>}
    </Box>
  );
}
